import { useCallback, useEffect, useRef } from 'react';

// Following code is based on the solution provided here https://martijnhols.nl/gists/how-to-get-document-height-ios-safari-osk
// It detects viewport size changes even when dealing with the OSK and iOS based mobile devices.

type Size = { width: number; height: number };

/**
 * Get the current size of the Viewport. Do not call this excessively, as it may
 * cause performance issues in WebKit. Querying innerWidth/height triggers a
 * re-layout of the page.
 */
export const getViewportSize = (): Size => {
  if (window.visualViewport) {
    // visualViewport is a new prop intended for this exact behavior, prefer it
    // over all else when available
    // https://developer.mozilla.org/en-US/docs/Web/API/Visual_Viewport_API
    return { width: window.visualViewport.width, height: window.visualViewport.height } as const;
  }

  // window.innerHeight gets updated when a user opens the soft keyboard, so
  // it should be preferred over documentElement.clientHeight
  // Want more? https://blog.opendigerati.com/the-eccentric-ways-of-ios-safari-with-the-keyboard-b5aa3f34228d
  return { width: window.innerWidth, height: window.innerHeight } as const;
};

export type UseRegisterViewportSizeListenersParams = {
  onViewportChange: (size: { width: number; height: number }) => void;
};

export const useRegisterViewportSizeListeners = ({ onViewportChange }: UseRegisterViewportSizeListenersParams) => {
  const currentSize = useRef<Size | null>(null);
  const prevSize = useRef<Size | null>(null);

  const handleViewportChange = useCallback(() => {
    prevSize.current = currentSize.current ? { ...currentSize.current } : currentSize.current;
    currentSize.current = getViewportSize();

    if (
      (!prevSize.current && currentSize.current) ||
      (prevSize.current &&
        currentSize.current &&
        (prevSize.current.width !== currentSize.current.width ||
          prevSize.current.height !== currentSize.current.height))
    ) {
      onViewportChange({ ...currentSize.current });
    }
  }, [onViewportChange]);

  useEffect(() => {
    if (window.visualViewport) {
      // This is needed on iOS to resize the viewport when the Virtual/OnScreen
      // Keyboard opens. This does not trigger any other event, or the standard
      // resize event.
      window.visualViewport.addEventListener('resize', handleViewportChange);
    } else {
      window.addEventListener('resize', handleViewportChange);
    }

    // This used to be required for older browsers since
    // this didn't trigger a resize event. Keeping it in to be safe.
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      window.removeEventListener('orientationchange', handleViewportChange);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      } else {
        window.removeEventListener('resize', handleViewportChange);
      }
    };
  }, [handleViewportChange]);
};
